import React, { useState, useEffect, useCallback } from 'react';
import {
  Col,
  Typography,
  Space,
  Button,
  Table,
  Spin,
  Alert,
  DatePicker,
  theme,
} from 'antd';
import {
  FileTextOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  ExceptionOutlined,
  ShopOutlined,
  InboxOutlined,
  BarChartOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { poMilestoneService } from '@/services/poMilestone.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import {
  getSupplyChainDashboardActionSummary,
  emptySupplyChainDashboardSummary,
  type SupplyChainDashboardActionSummary,
  type PendingApprovalPRRow,
  type PendingApprovalBidAnalysisRow,
  type PendingApprovalPORow,
  type PendingApprovalGrnRow,
  type PendingApprovalReturnRow,
  type PRPendingSourcingRow,
  type GrnNotInvoicedRow,
} from '@/services/supplyChainDashboard.service';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import { ControlTowerSubQueuePanel } from '@/components/control-tower/ControlTowerSubQueuePanel';
import { ScmAttentionBrief } from '@/components/scm/control-tower/ScmAttentionBrief';
import { OverduePoFollowUpZone } from '@/components/scm/control-tower/OverduePoFollowUpZone';

const { Title, Text, Paragraph } = Typography;

const scrollToSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const emptySummary = emptySupplyChainDashboardSummary();

export const SupplyChainDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { formatCurrency } = useCurrency();
  const [summary, setSummary] = useState<SupplyChainDashboardActionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [asOnDate, setAsOnDate] = useState<string | undefined>(undefined);
  const [delayedMode, setDelayedMode] = useState<'overdue' | 'due_soon'>('overdue');

  const { data: milestoneSummary } = useQuery({
    queryKey: ['po-milestone-dashboard-summary'],
    queryFn: () => poMilestoneService.dashboardSummary(),
  });

  const fetchSummary = useCallback(() => {
    setLoading(true);
    setError(null);
    getSupplyChainDashboardActionSummary(asOnDate ? { as_on_date: asOnDate } : undefined)
      .then(setSummary)
      .catch((e: any) => setError(e?.message ?? 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [asOnDate]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  if (loading && !summary) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const s = summary ?? emptySummary;
  const overdueCount = s.overdue_po_count ?? 0;
  const dueSoonCount = s.due_soon_po_count ?? 0;
  const pendingApprovalTotal =
    s.pending_approval_prs.length +
    s.pending_approval_bid_analyses.length +
    s.pending_approval_pos.length +
    s.pending_approval_grns.length +
    s.pending_approval_returns.length;

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
            position: 'sticky',
            top: 0,
            zIndex: 10,
            background: token.colorBgContainer,
            paddingBottom: 8,
          }}
        >
          <div>
            <Title level={3} style={{ marginBottom: 0 }}>
              Supply Chain Control Tower
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 4 }}>
              Action-driven view: attention brief, full overdue PO follow-up, approvals, sourcing, and receipts.
            </Paragraph>
            <Space wrap size={[4, 4]}>
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => scrollToSection('attention-brief')}>
                Attention
              </Button>
              <Text type="secondary">·</Text>
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => scrollToSection('overdue-pos')}>
                Delayed POs
              </Button>
              <Text type="secondary">·</Text>
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => scrollToSection('pending-approvals')}>
                Approvals
              </Button>
              <Text type="secondary">·</Text>
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => scrollToSection('prs-sourcing')}>
                Sourcing
              </Button>
              <Text type="secondary">·</Text>
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => scrollToSection('grn-not-invoiced')}>
                GRN bills
              </Button>
            </Space>
          </div>
          <Space wrap>
            <Space>
              <Text type="secondary">As on date:</Text>
              <DatePicker
                value={asOnDate ? dayjs(asOnDate) : dayjs()}
                onChange={(d) => setAsOnDate(d ? d.format('YYYY-MM-DD') : undefined)}
                allowClear
              />
            </Space>
            <Button icon={<ReloadOutlined />} onClick={fetchSummary} loading={loading}>
              Refresh
            </Button>
          </Space>
        </div>

        {error && <Alert type="error" message={error} />}

        <ScmAttentionBrief
          summary={s}
          formatCurrency={formatCurrency}
          pendingApprovalTotal={pendingApprovalTotal}
          milestonesDue={milestoneSummary?.dueCount ?? 0}
        />

        <ControlTowerSectionCard zone="actions" title="Quick Actions">
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/scm/procurement/purchase-requests/new')}>
              Create PR
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/scm/procurement/supplier-inquiries/new')}>
              Create Inquiry
            </Button>
            <Button icon={<FileTextOutlined />} onClick={() => navigate('/scm/procurement/supplier-quotations')}>
              Supplier Quotations
            </Button>
            <Button icon={<FileTextOutlined />} onClick={() => navigate('/scm/procurement/bid-analyses')}>
              Bid Analyses
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/scm/procurement/purchase-orders/new')}>
              Create PO
            </Button>
            <Button icon={<InboxOutlined />} onClick={() => navigate('/scm/procurement/grn/new')}>
              Create GRN
            </Button>
            <Button icon={<InboxOutlined />} onClick={() => navigate('/scm/procurement/purchase-returns/new')}>
              Create Purchase Return
            </Button>
            <Button icon={<ShopOutlined />} onClick={() => navigate('/scm/procurement/suppliers')}>
              Open Suppliers
            </Button>
            <Button icon={<BarChartOutlined />} onClick={() => navigate('/scm/inventory/stock-summary')}>
              Stock Summary
            </Button>
            <Button icon={<InboxOutlined />} onClick={() => navigate('/scm/procurement/grn')}>
              Pending Receipts
            </Button>
          </Space>
        </ControlTowerSectionCard>

        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <ControlTowerMetricStrip zone="metricsPayables" title="PO delivery risk">
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Overdue POs"
                count={overdueCount}
                amount={s.overdue_po_value}
                formatCurrency={formatCurrency}
                icon={<WarningOutlined />}
                urgency={overdueCount > 0 ? 'critical' : 'neutral'}
                extra={
                  overdueCount > 0 && (s.overdue_aging?.['30+'] ?? 0) > 0 ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {s.overdue_aging['30+']} aged 30+
                    </Text>
                  ) : undefined
                }
                footerLink={
                  overdueCount > 0
                    ? {
                        label: 'Follow up all',
                        onClick: () => {
                          setDelayedMode('overdue');
                          scrollToSection('overdue-pos');
                        },
                      }
                    : undefined
                }
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Due soon POs"
                count={dueSoonCount}
                amount={s.due_soon_po_value}
                formatCurrency={formatCurrency}
                icon={<ClockCircleOutlined />}
                urgency={dueSoonCount > 0 ? 'attention' : 'neutral'}
                footerLink={
                  dueSoonCount > 0
                    ? {
                        label: 'View queue',
                        onClick: () => {
                          setDelayedMode('due_soon');
                          scrollToSection('overdue-pos');
                        },
                      }
                    : undefined
                }
              />
            </Col>
          </ControlTowerMetricStrip>

          <ControlTowerMetricStrip zone="metricsDocPrep" title="Workflow">
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Pending approvals"
                count={pendingApprovalTotal}
                icon={<ExceptionOutlined />}
                urgency={pendingApprovalTotal > 0 ? 'attention' : 'neutral'}
                footerLink={{ label: 'View queue', onClick: () => scrollToSection('pending-approvals') }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="GRN not invoiced"
                count={s.grn_not_invoiced.length}
                icon={<WarningOutlined />}
                urgency={s.grn_not_invoiced.length > 0 ? 'attention' : 'neutral'}
                footerLink={{ label: 'View queue', onClick: () => scrollToSection('grn-not-invoiced') }}
              />
            </Col>
          </ControlTowerMetricStrip>

          <div id="po-milestones">
            <ControlTowerMetricStrip zone="metricsDocPrep" title="PO milestones">
              <Col xs={24} sm={12} md={6}>
                <ControlTowerMetricTile
                  title="Milestones due"
                  count={milestoneSummary?.dueCount ?? 0}
                  icon={<ClockCircleOutlined />}
                  urgency={(milestoneSummary?.dueCount ?? 0) > 0 ? 'attention' : 'neutral'}
                  footerLink={{
                    label: 'Register',
                    onClick: () => navigate('/scm/procurement/reports/po-milestones'),
                  }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <ControlTowerMetricTile
                  title="Pending acceptance"
                  count={milestoneSummary?.pendingAcceptance ?? 0}
                  icon={<ExceptionOutlined />}
                  urgency={(milestoneSummary?.pendingAcceptance ?? 0) > 0 ? 'attention' : 'neutral'}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <ControlTowerMetricTile
                  title="Receipts pending"
                  count={milestoneSummary?.pendingReceipts ?? 0}
                  icon={<InboxOutlined />}
                  urgency={(milestoneSummary?.pendingReceipts ?? 0) > 0 ? 'attention' : 'neutral'}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <ControlTowerMetricTile
                  title="Invoices pending"
                  count={milestoneSummary?.pendingInvoices ?? 0}
                  icon={<FileTextOutlined />}
                  urgency={(milestoneSummary?.pendingInvoices ?? 0) > 0 ? 'attention' : 'neutral'}
                />
              </Col>
            </ControlTowerMetricStrip>
          </div>

          <ControlTowerMetricStrip zone="metricsOrders" title="Sourcing">
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="PRs pending sourcing"
                count={s.prs_pending_sourcing.length}
                icon={<FileTextOutlined />}
                urgency={s.prs_pending_sourcing.length > 0 ? 'attention' : 'neutral'}
                footerLink={
                  s.prs_pending_sourcing.length > 0
                    ? { label: 'View queue', onClick: () => scrollToSection('prs-sourcing') }
                    : undefined
                }
              />
            </Col>
          </ControlTowerMetricStrip>

          <ControlTowerMetricStrip zone="metricsDrafts" title="Drafts">
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Draft PRs"
                count={s.draft_pr_count}
                icon={<FileTextOutlined />}
                urgency={s.draft_pr_count > 0 ? 'attention' : 'neutral'}
                footerLink={
                  s.draft_pr_count > 0
                    ? { label: 'Open', onClick: () => navigate('/scm/procurement/purchase-requests') }
                    : undefined
                }
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Draft POs"
                count={s.draft_po_count}
                icon={<ShoppingCartOutlined />}
                urgency={s.draft_po_count > 0 ? 'attention' : 'neutral'}
                footerLink={
                  s.draft_po_count > 0
                    ? { label: 'Open', onClick: () => navigate('/scm/procurement/purchase-orders') }
                    : undefined
                }
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Draft GRNs"
                count={s.draft_grn_count}
                icon={<InboxOutlined />}
                urgency={s.draft_grn_count > 0 ? 'attention' : 'neutral'}
                footerLink={
                  s.draft_grn_count > 0
                    ? { label: 'Open', onClick: () => navigate('/scm/procurement/grn') }
                    : undefined
                }
              />
            </Col>
          </ControlTowerMetricStrip>
        </Space>

        <OverduePoFollowUpZone
          summary={s}
          asOnDate={asOnDate}
          formatCurrency={formatCurrency}
          initialMode={delayedMode}
        />

        <ControlTowerSectionCard
          zone="pendingApprovals"
          sectionId="pending-approvals"
          title={
            <Space>
              <ExceptionOutlined />
              <span>Pending approvals ({pendingApprovalTotal})</span>
            </Space>
          }
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <ControlTowerSubQueuePanel zone="scmApprovalPr" title="Purchase requests" count={s.pending_approval_prs.length}>
              {s.pending_approval_prs.length === 0 ? (
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  None
                </Paragraph>
              ) : (
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={s.pending_approval_prs}
                  columns={[
                    { title: 'PR #', dataIndex: 'pr_number', key: 'pr_number', width: 120 },
                    { title: 'Required', dataIndex: 'required_date', key: 'required_date', width: 100 },
                    {
                      title: 'Est. total',
                      dataIndex: 'estimated_total',
                      key: 'estimated_total',
                      align: 'right' as const,
                      render: (v: number) => formatCurrency(v ?? 0),
                    },
                    {
                      title: '',
                      key: 'action',
                      width: 70,
                      render: (_: unknown, r: PendingApprovalPRRow) => (
                        <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/purchase-requests/${r.id}`)}>
                          View
                        </Button>
                      ),
                    },
                  ]}
                />
              )}
            </ControlTowerSubQueuePanel>
            <ControlTowerSubQueuePanel zone="scmApprovalBid" title="Bid analyses" count={s.pending_approval_bid_analyses.length}>
              {s.pending_approval_bid_analyses.length === 0 ? (
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  None
                </Paragraph>
              ) : (
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={s.pending_approval_bid_analyses}
                  columns={[
                    { title: 'Analysis #', dataIndex: 'analysis_number', key: 'analysis_number', width: 120 },
                    {
                      title: '',
                      key: 'action',
                      width: 70,
                      render: (_: unknown, r: PendingApprovalBidAnalysisRow) => (
                        <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/bid-analyses/${r.id}`)}>
                          View
                        </Button>
                      ),
                    },
                  ]}
                />
              )}
            </ControlTowerSubQueuePanel>
            <ControlTowerSubQueuePanel zone="scmApprovalPo" title="Purchase orders" count={s.pending_approval_pos.length}>
              {s.pending_approval_pos.length === 0 ? (
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  None
                </Paragraph>
              ) : (
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={s.pending_approval_pos}
                  columns={[
                    { title: 'PO #', dataIndex: 'po_number', key: 'po_number', width: 120 },
                    { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name', ellipsis: true },
                    {
                      title: 'Amount',
                      dataIndex: 'grand_total',
                      key: 'grand_total',
                      align: 'right' as const,
                      render: (v: number) => formatCurrency(v ?? 0),
                    },
                    {
                      title: '',
                      key: 'action',
                      width: 70,
                      render: (_: unknown, r: PendingApprovalPORow) => (
                        <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/purchase-orders/${r.id}`)}>
                          View
                        </Button>
                      ),
                    },
                  ]}
                />
              )}
            </ControlTowerSubQueuePanel>
            <ControlTowerSubQueuePanel zone="scmApprovalGrn" title="GRNs" count={s.pending_approval_grns.length}>
              {s.pending_approval_grns.length === 0 ? (
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  None
                </Paragraph>
              ) : (
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={s.pending_approval_grns}
                  columns={[
                    { title: 'GRN #', dataIndex: 'grn_number', key: 'grn_number', width: 120 },
                    { title: 'Date', dataIndex: 'grn_date', key: 'grn_date', width: 100 },
                    {
                      title: '',
                      key: 'action',
                      width: 70,
                      render: (_: unknown, r: PendingApprovalGrnRow) => (
                        <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/grn/${r.id}`)}>
                          View
                        </Button>
                      ),
                    },
                  ]}
                />
              )}
            </ControlTowerSubQueuePanel>
            <ControlTowerSubQueuePanel zone="scmApprovalReturn" title="Purchase returns" count={s.pending_approval_returns.length}>
              {s.pending_approval_returns.length === 0 ? (
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  None
                </Paragraph>
              ) : (
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={s.pending_approval_returns}
                  columns={[
                    { title: 'Return #', dataIndex: 'return_number', key: 'return_number', width: 120 },
                    { title: 'Date', dataIndex: 'return_date', key: 'return_date', width: 100 },
                    {
                      title: '',
                      key: 'action',
                      width: 70,
                      render: (_: unknown, r: PendingApprovalReturnRow) => (
                        <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/purchase-returns/${r.id}`)}>
                          View
                        </Button>
                      ),
                    },
                  ]}
                />
              )}
            </ControlTowerSubQueuePanel>
          </Space>
        </ControlTowerSectionCard>

        <ControlTowerSectionCard zone="sourcingQueue" sectionId="prs-sourcing" title="PRs pending sourcing">
          {s.prs_pending_sourcing.length === 0 ? (
            <Text type="secondary">No PRs pending sourcing.</Text>
          ) : (
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={s.prs_pending_sourcing}
              columns={[
                { title: 'PR #', dataIndex: 'pr_number', key: 'pr_number', width: 120 },
                { title: 'Required date', dataIndex: 'required_date', key: 'required_date', width: 110 },
                {
                  title: 'Est. total',
                  dataIndex: 'estimated_total',
                  key: 'estimated_total',
                  align: 'right' as const,
                  render: (v: number) => formatCurrency(v ?? 0),
                },
                {
                  title: '',
                  key: 'action',
                  width: 80,
                  render: (_: unknown, r: PRPendingSourcingRow) => (
                    <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/purchase-requests/${r.id}`)}>
                      View
                    </Button>
                  ),
                },
              ]}
            />
          )}
        </ControlTowerSectionCard>

        <ControlTowerSectionCard
          zone="documentPrep"
          sectionId="grn-not-invoiced"
          title={
            <Space>
              <InboxOutlined />
              <span>Supplier bills to create (GRNs ready)</span>
            </Space>
          }
        >
          {s.grn_not_invoiced.length === 0 ? (
            <Text type="secondary">None ready</Text>
          ) : (
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={s.grn_not_invoiced}
              columns={[
                { title: 'GRN', dataIndex: 'grn_number', key: 'grn_number', width: 120 },
                { title: 'Date', dataIndex: 'grn_date', key: 'grn_date', width: 100 },
                { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name', ellipsis: true },
                {
                  title: '',
                  key: 'action',
                  width: 140,
                  render: (_: unknown, r: GrnNotInvoicedRow) => (
                    <Space size="small">
                      <Button
                        type="link"
                        size="small"
                        onClick={() => navigate(`/scm/procurement/supplier-invoices/new?grn_id=${r.id}`)}
                      >
                        Create bill
                      </Button>
                      <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/grn/${r.id}`)}>
                        GRN
                      </Button>
                    </Space>
                  ),
                },
              ]}
            />
          )}
        </ControlTowerSectionCard>
      </Space>
    </div>
  );
};
